import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Trash2, Plus, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MappingEndpointsSectionProps {
  mappingId: string;
  parentBotId: string;
  parentBotName: string;
  childBotName: string;
}

interface BotSignalForwardEndpoint {
  id: string;
  bot_id?: string;
  mapping_id?: string;
  url: string;
  name: string;
  timeout_ms: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const MappingEndpointsSection: React.FC<MappingEndpointsSectionProps> = ({
  mappingId,
  parentBotId,
  parentBotName,
  childBotName,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddingEndpoint, setIsAddingEndpoint] = useState(false);
  const [newEndpoint, setNewEndpoint] = useState({
    name: '',
    url: '',
    timeout_ms: 30000,
    is_active: true,
  });

  // Fetch mapping-specific endpoints
  const { data: mappingEndpoints, isLoading: loadingMappingEndpoints } = useQuery({
    queryKey: ['mapping-endpoints', mappingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bot_signal_forward_endpoints')
        .select('*')
        .eq('mapping_id', mappingId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Fetch parent bot endpoints (for reference only)
  const { data: parentEndpoints, isLoading: loadingParentEndpoints } = useQuery({
    queryKey: ['parent-bot-endpoints', parentBotId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bot_signal_forward_endpoints')
        .select('*')
        .eq('bot_id', parentBotId)
        .is('mapping_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Create mapping endpoint mutation
  const createEndpointMutation = useMutation({
    mutationFn: async (endpoint: typeof newEndpoint) => {
      const { data, error } = await supabase
        .from('bot_signal_forward_endpoints')
        .insert({
          mapping_id: mappingId,
          name: endpoint.name,
          url: endpoint.url,
          timeout_ms: endpoint.timeout_ms,
          is_active: endpoint.is_active,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mapping-endpoints', mappingId] });
      setIsAddingEndpoint(false);
      setNewEndpoint({
        name: '',
        url: '',
        timeout_ms: 30000,
        is_active: true,
      });
      toast({
        title: "Endpoint được tạo thành công",
        description: "Endpoint mới đã được thêm cho mapping này.",
      });
    },
    onError: (error) => {
      toast({
        title: "Lỗi tạo endpoint",
        description: `Không thể tạo endpoint: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Update endpoint mutation
  const updateEndpointMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<BotSignalForwardEndpoint> }) => {
      const { data, error } = await supabase
        .from('bot_signal_forward_endpoints')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mapping-endpoints', mappingId] });
      toast({
        title: "Endpoint được cập nhật",
        description: "Thông tin endpoint đã được lưu.",
      });
    },
    onError: (error) => {
      toast({
        title: "Lỗi cập nhật endpoint",
        description: `Không thể cập nhật endpoint: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Delete endpoint mutation
  const deleteEndpointMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bot_signal_forward_endpoints')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mapping-endpoints', mappingId] });
      toast({
        title: "Endpoint đã xóa",
        description: "Endpoint đã được xóa khỏi mapping.",
      });
    },
    onError: (error) => {
      toast({
        title: "Lỗi xóa endpoint",
        description: `Không thể xóa endpoint: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const handleCreateEndpoint = () => {
    if (!newEndpoint.name.trim() || !newEndpoint.url.trim()) {
      toast({
        title: "Thiếu thông tin",
        description: "Vui lòng nhập tên và URL endpoint.",
        variant: "destructive",
      });
      return;
    }

    createEndpointMutation.mutate(newEndpoint);
  };

  const handleToggleEndpoint = (id: string, currentStatus: boolean) => {
    updateEndpointMutation.mutate({
      id,
      updates: { is_active: !currentStatus },
    });
  };

  const handleDeleteEndpoint = (id: string) => {
    if (confirm('Bạn có chắc chắn muốn xóa endpoint này?')) {
      deleteEndpointMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Mapping-specific endpoints */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Endpoints cho Mapping ({childBotName} → {parentBotName})</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAddingEndpoint(true)}
              disabled={isAddingEndpoint}
            >
              <Plus className="h-4 w-4 mr-2" />
              Thêm Endpoint
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new endpoint form */}
          {isAddingEndpoint && (
            <Card className="p-4 border-dashed">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="endpoint_name">Tên Endpoint</Label>
                    <Input
                      id="endpoint_name"
                      value={newEndpoint.name}
                      onChange={(e) => setNewEndpoint(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="VD: Primary Trading API"
                    />
                  </div>
                  <div>
                    <Label htmlFor="endpoint_url">URL Endpoint</Label>
                    <Input
                      id="endpoint_url"
                      value={newEndpoint.url}
                      onChange={(e) => setNewEndpoint(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://api.example.com/webhook"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="timeout_ms">Timeout (ms)</Label>
                  <Input
                    id="timeout_ms"
                    type="number"
                    value={newEndpoint.timeout_ms}
                    onChange={(e) => setNewEndpoint(prev => ({ ...prev, timeout_ms: parseInt(e.target.value) || 30000 }))}
                    placeholder="30000"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={newEndpoint.is_active}
                    onCheckedChange={(checked) => setNewEndpoint(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="is_active">Kích hoạt endpoint</Label>
                </div>
                <div className="flex space-x-2">
                  <Button onClick={handleCreateEndpoint} disabled={createEndpointMutation.isPending}>
                    {createEndpointMutation.isPending ? 'Đang tạo...' : 'Tạo Endpoint'}
                  </Button>
                  <Button variant="outline" onClick={() => setIsAddingEndpoint(false)}>
                    Hủy
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Mapping endpoints list */}
          {loadingMappingEndpoints ? (
            <div>Đang tải endpoints...</div>
          ) : mappingEndpoints?.length ? (
            <div className="space-y-2">
              {mappingEndpoints.map((endpoint) => (
                <Card key={endpoint.id} className="p-4">
                  <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium">{endpoint.name}</h4>
                      <Switch
                        checked={endpoint.is_active}
                        onCheckedChange={() => handleToggleEndpoint(endpoint.id, endpoint.is_active)}
                        disabled={updateEndpointMutation.isPending}
                      />
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center space-x-1">
                      <ExternalLink className="h-3 w-3" />
                      <span>{endpoint.url}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">Timeout: {endpoint.timeout_ms}ms</p>
                  </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteEndpoint(endpoint.id)}
                      disabled={deleteEndpointMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                Chưa có endpoint nào cho mapping này. Signals sẽ chỉ được ghi log và KHÔNG được chuyển tiếp đi đâu.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Parent bot endpoints (read-only reference) */}
      <Card>
        <CardHeader>
          <CardTitle>Endpoints Bot Cha ({parentBotName}) - Tham khảo</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingParentEndpoints ? (
            <div>Đang tải endpoints bot cha...</div>
          ) : parentEndpoints?.length ? (
            <div className="space-y-2">
              {parentEndpoints.map((endpoint) => (
                <Card key={endpoint.id} className="p-4 opacity-60">
                  <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium">{endpoint.name}</h4>
                      <span className={`px-2 py-1 text-xs rounded ${endpoint.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {endpoint.is_active ? 'Hoạt động' : 'Tạm dừng'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground flex items-center space-x-1">
                      <ExternalLink className="h-3 w-3" />
                      <span>{endpoint.url}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">Timeout: {endpoint.timeout_ms}ms</p>
                  </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                Bot cha chưa có endpoints riêng. Chỉ endpoints của mapping này mới được sử dụng để chuyển tiếp signals.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};